/**
 * Hooks para gestión de CVUs (Pronto Pago) — Fase 6.1
 *
 * Todas las operaciones van vía `api/` (axios) porque requieren `service_role`
 * para hablar con Pronto Pago y persistir en `cvu_empresas` — ver #014.
 *
 * El backend devuelve `{ data, mode }` donde `mode = 'mock' | 'real'`. El
 * hook `useCvuConfig` se usa para mostrar el banner "MODO MOCK" en la UI.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  Cvu,
  CvuConfig,
  CvuSyncReport,
  CvuLoteResult,
  ProntoPagoMode,
} from '../types/cvu';

export const QK_CVUS = {
  all:    ['cvus'] as const,
  config: ['cvus', 'config'] as const,
};

// ── LIST ──────────────────────────────────────────────────────────────────────

export interface UseCvusResult {
  cvus: Cvu[];
  mode: ProntoPagoMode;
}

export function useCvus() {
  return useQuery<UseCvusResult>({
    queryKey: QK_CVUS.all,
    queryFn: async () => {
      const { data } = await api.get<{ data: Cvu[]; mode: ProntoPagoMode }>('/cvu');
      return { cvus: data.data, mode: data.mode };
    },
  });
}

// ── CONFIG ────────────────────────────────────────────────────────────────────

export function useCvuConfig() {
  return useQuery<CvuConfig>({
    queryKey: QK_CVUS.config,
    queryFn: async () => {
      const { data } = await api.get<{ data: CvuConfig }>('/cvu/config');
      return data.data;
    },
    staleTime: 5 * 60_000, // raramente cambia
  });
}

// ── TEST CONNECTION ───────────────────────────────────────────────────────────

export function useTestConexion() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<{ data: { ok: boolean; mode: ProntoPagoMode } }>(
        '/cvu/test-connection',
      );
      return data.data;
    },
  });
}

// ── GENERAR CVU INDIVIDUAL ────────────────────────────────────────────────────

export function useGenerarCvu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (empresaId: string) => {
      const { data } = await api.post<{ data: Cvu; warning?: string | null; mode: ProntoPagoMode }>(
        `/cvu/generar/${empresaId}`,
      );
      return { cvu: data.data, warning: data.warning ?? null };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_CVUS.all }),
  });
}

// ── GENERAR LOTE (máx 5) ──────────────────────────────────────────────────────

export function useGenerarCvuLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      empresa_ids,
      id_lote,
    }: {
      empresa_ids: string[];
      id_lote?: string;
    }) => {
      const { data } = await api.post<{ data: CvuLoteResult; mode: ProntoPagoMode }>(
        '/cvu/lote',
        { empresa_ids, id_lote },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_CVUS.all }),
  });
}

// ── DESACTIVAR / REACTIVAR ────────────────────────────────────────────────────

export function useDesactivarCvu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (empresaId: string) => {
      const { data } = await api.put<{ data: Cvu; message: string }>(
        `/cvu/${empresaId}/desactivar`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_CVUS.all }),
  });
}

export function useReactivarCvu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (empresaId: string) => {
      const { data } = await api.put<{ data: Cvu; message: string }>(
        `/cvu/${empresaId}/reactivar`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_CVUS.all }),
  });
}

// ── SINCRONIZAR ───────────────────────────────────────────────────────────────

export function useSincronizarCvus() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ data: CvuSyncReport; mode: ProntoPagoMode }>(
        '/cvu/sync',
      );
      return data.data;
    },
  });
}

// ── CONSULTAR TRANSACCIONES DEL DÍA (PULL contra PP) ──────────────────────────
//
// Llama al endpoint `Webhook Status Trx` de Pronto Pago a través del backend.
// Solo devuelve transacciones del día actual (ventana 00:00 a 24:00 de PP).
// El shape del response no está formalmente tipado (postman v2 no incluye
// ejemplo) — la UI lo muestra como JSON pretty hasta que tipemos.

export interface ConsultaTransaccionesResult {
  data: unknown;
  mode: ProntoPagoMode;
}

export function useConsultarTransaccionesHoy() {
  return useMutation({
    mutationFn: async (empresaId: string) => {
      const { data } = await api.get<ConsultaTransaccionesResult>(
        `/cvu/${empresaId}/transacciones-hoy`,
      );
      return data;
    },
  });
}

// ── CVU DE UNA EMPRESA (lectura directa, OK para admin_empresa por RLS) ──────
import { supabase as supaClient } from '../services/supabase';

export function useCvuDeEmpresa(empresaId: string | undefined) {
  return useQuery({
    queryKey: ['cvus', 'by-empresa', empresaId],
    queryFn: async () => {
      const { data, error } = await supaClient
        .from('cvu_empresas')
        .select('id, empresa_id, cvu, alias, name, external_id, enabled, estado')
        .eq('empresa_id', empresaId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Cvu | null;
    },
    enabled: !!empresaId,
  });
}

// ── EMPRESAS SIN CVU (para los dialogs de generación) ─────────────────────────
// Devuelve la lista de empresas activas que aún no tienen CVU asignada.
// Lectura directa a Supabase (CRUD común — #014).

import { supabase } from '../services/supabase';
import type { EmpresaSimple } from './useUsuarios';

export function useEmpresasSinCvu() {
  return useQuery({
    queryKey: ['empresas', 'sin-cvu'],
    queryFn: async () => {
      const { data: empresas, error: errEmp } = await supabase
        .from('empresas')
        .select('id, razon_social, cuit')
        .eq('estado', 'activa')
        .order('razon_social', { ascending: true });
      if (errEmp) throw errEmp;

      const { data: cvus, error: errCvu } = await supabase
        .from('cvu_empresas')
        .select('empresa_id');
      if (errCvu) throw errCvu;

      const conCvu = new Set((cvus ?? []).map((c) => c.empresa_id));
      return (empresas ?? []).filter((e) => !conCvu.has(e.id)) as EmpresaSimple[];
    },
  });
}
